// Wires the client-side QR decoder widget, shared by the analytics-checker and
// url-checker tools. The uploaded/dropped image is drawn to a <canvas> and
// decoded locally with jsQR — nothing is uploaded. `data-qr-decode-mode`
// ("analytics" | "url") only changes the verdict copy.
import jsQR from "jsqr"

const MAX_DIM = 1000 // downscale large photos before decoding (speed)

// Is this a TangoQR / short-link redirect (trackable + editable)?
function isTangoRedirect(text) {
  return /^https?:\/\/(www\.)?(tgo\.sh\/|tangoqr\.com\/r\/)/i.test(text)
}

function isUrl(text) {
  return /^https?:\/\//i.test(text)
}

function verdictFor(text, mode) {
  const wrap = (cls, heading, body) =>
    `<p class="font-display font-semibold ${cls}">${heading}</p><p class="mt-1 text-[color:var(--color-muted)] dark:text-muted-light">${body}</p>`

  if (isTangoRedirect(text)) {
    return wrap(
      "text-voltage-dark dark:text-voltage-light",
      "This is an editable, trackable code.",
      "It points at a redirect, so it can report scans and its destination can be changed without reprinting."
    )
  }

  if (isUrl(text)) {
    const upsell =
      ' To get scan analytics and the ability to repoint it later, make an <a href="/" class="text-voltage-dark dark:text-voltage-light underline">editable TangoQR code</a> instead.'
    if (mode === "url") {
      return wrap(
        "text-coal dark:text-cloud",
        "This is a direct, static link.",
        "Check the domain above looks legitimate before you open it. The link is baked into the image, so it can't be changed or tracked." + upsell
      )
    }
    return wrap(
      "text-coal dark:text-cloud",
      "This is a static code. It can't report scans.",
      "The link is baked straight into the image. A static code has no redirect behind it, so there's nothing to count scans or edit." + upsell
    )
  }

  return wrap(
    "text-coal dark:text-cloud",
    "This code holds text or data, not a web link.",
    "It encodes content directly (for example WiFi details or a contact card), so there's no destination to track or change."
  )
}

function setupWidget(widget) {
  const fileInput = widget.querySelector("[data-qr-file]")
  const dropzone = widget.querySelector("[data-qr-dropzone]")
  const result = widget.querySelector("[data-qr-decode-result]")
  const urlEl = widget.querySelector("[data-qr-decode-url]")
  const verdictEl = widget.querySelector("[data-qr-decode-verdict]")
  const errorEl = widget.querySelector("[data-qr-decode-error]")
  const mode = widget.dataset.qrDecodeMode

  function fail() {
    if (result) result.hidden = true
    if (errorEl) errorEl.hidden = false
  }

  function show(text) {
    if (errorEl) errorEl.hidden = true
    urlEl.textContent = text
    verdictEl.className = "mt-4 rounded-xl p-4 text-sm bg-cloud dark:bg-coal border border-[color:var(--color-border)] dark:border-storm"
    verdictEl.innerHTML = verdictFor(text, mode)
    result.hidden = false
  }

  function handleFile(file) {
    if (!file || !file.type.startsWith("image/")) return fail()
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(objectUrl)
      const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height))
      const canvas = document.createElement("canvas")
      canvas.width = Math.max(1, Math.round(img.width * scale))
      canvas.height = Math.max(1, Math.round(img.height * scale))
      const ctx = canvas.getContext("2d")
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      let data
      try {
        data = ctx.getImageData(0, 0, canvas.width, canvas.height)
      } catch {
        return fail()
      }
      const code = jsQR(data.data, data.width, data.height)
      if (code && code.data) show(code.data)
      else fail()
    }
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      fail()
    }
    img.src = objectUrl
  }

  fileInput?.addEventListener("change", (e) => handleFile(e.target.files[0]))

  if (dropzone) {
    ;["dragover", "dragenter"].forEach((ev) =>
      dropzone.addEventListener(ev, (e) => {
        e.preventDefault()
        dropzone.dataset.drag = "true"
      })
    )
    ;["dragleave", "dragend"].forEach((ev) =>
      dropzone.addEventListener(ev, () => delete dropzone.dataset.drag)
    )
    dropzone.addEventListener("drop", (e) => {
      e.preventDefault()
      delete dropzone.dataset.drag
      handleFile(e.dataTransfer?.files?.[0])
    })
  }
}

export function setupQrDecoders() {
  document.querySelectorAll('[data-qr-widget="decoder"]').forEach(setupWidget)
}
